/**
 * FieldModeSessionsTable - Saha oturumlarını (sahaya çıkış → ofise dönüş) tablo halinde gösterir.
 * Hem Saha Modu Talepleri sayfasında (şirket geneli, Çalışan kolonu ile) hem de
 * Personel Aktivite Detay sayfasında (tek çalışan) kullanılır.
 */

import React, { useEffect, useState } from 'react';
import { Avatar, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FieldModeSession } from '../../../../types/fieldModeRequest.types';
import { getFileUrl } from '../../../../utils/file.utils';

const { Text } = Typography;

const formatDateTime = (value?: string | null) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-');

export const formatDurationMinutes = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '-';
  const safe = Math.max(0, Math.floor(minutes));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours === 0) return `${mins} dk`;
  return mins === 0 ? `${hours} sa` : `${hours} sa ${mins} dk`;
};

/** Talep zamanı gerçekleşme zamanından farklı dakikadaysa küçük "talep: HH:mm" notu gösterilir */
const RequestedAtHint: React.FC<{ requestedAt: string | null; actualAt: string | null }> = ({ requestedAt, actualAt }) => {
  if (!requestedAt || !actualAt) return null;
  if (dayjs(requestedAt).format('YYYY-MM-DD HH:mm') === dayjs(actualAt).format('YYYY-MM-DD HH:mm')) return null;
  const sameDay = dayjs(requestedAt).isSame(dayjs(actualAt), 'day');
  return (
    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
      talep: {dayjs(requestedAt).format(sameDay ? 'HH:mm' : 'DD.MM.YYYY HH:mm')}
    </Text>
  );
};

const getAvatarSrc = (user: FieldModeSession['user']) => {
  if (user.profilePicture && user.profilePicture.trim() !== '') return getFileUrl(user.profilePicture);
  if (user.gender === 'male') return '/assets/img/man.png';
  if (user.gender === 'female') return '/assets/img/woman.png';
  return undefined;
};

interface FieldModeSessionsTableProps {
  sessions: FieldModeSession[];
  loading?: boolean;
  /** Şirket geneli listede Çalışan kolonu gösterilir */
  showEmployee?: boolean;
}

const FieldModeSessionsTable: React.FC<FieldModeSessionsTableProps> = ({ sessions, loading, showEmployee = false }) => {
  // Devam eden oturumların süresi dakikada bir güncellensin
  const [now, setNow] = useState(() => dayjs());
  const hasOngoing = sessions.some((s) => s.isOngoing);

  useEffect(() => {
    if (!hasOngoing) return undefined;
    setNow(dayjs());
    const timer = setInterval(() => setNow(dayjs()), 60000);
    return () => clearInterval(timer);
  }, [hasOngoing]);

  const employeeColumn: ColumnsType<FieldModeSession> = showEmployee
    ? [
        {
          title: 'Çalışan',
          key: 'user',
          render: (_, record) => {
            const src = getAvatarSrc(record.user);
            return (
              <div className="d-flex align-items-center gap-2">
                <Avatar
                  size={36}
                  src={src}
                  icon={!src ? <UserOutlined /> : undefined}
                  style={{ backgroundColor: !src ? '#1890ff' : undefined, flexShrink: 0 }}
                />
                <div>
                  <div className="fw-medium">
                    {record.user.firstName} {record.user.lastName}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.user.primaryDepartment?.name || 'Departman Yok'}
                  </Text>
                </div>
              </div>
            );
          },
        },
      ]
    : [];

  const columns: ColumnsType<FieldModeSession> = [
    ...employeeColumn,
    {
      title: 'Sahaya Çıkış',
      key: 'startedAt',
      render: (_, record) =>
        record.startedAt ? (
          <div>
            <div>{formatDateTime(record.startedAt)}</div>
            <RequestedAtHint requestedAt={record.startRequestedAt} actualAt={record.startedAt} />
          </div>
        ) : (
          <Tag>Çıkış kaydı yok</Tag>
        ),
    },
    {
      title: 'Ofise Dönüş',
      key: 'endedAt',
      render: (_, record) => {
        if (record.isOngoing) return <Tag color="green">Sahada</Tag>;
        if (!record.endedAt) return <Tag color="orange">Dönüş kaydı yok</Tag>;
        return (
          <div>
            <div>{formatDateTime(record.endedAt)}</div>
            <RequestedAtHint requestedAt={record.endRequestedAt} actualAt={record.endedAt} />
          </div>
        );
      },
    },
    {
      title: 'Süre',
      key: 'duration',
      render: (_, record) => {
        if (record.isOngoing && record.startedAt) {
          return (
            <div>
              <div>{formatDurationMinutes(now.diff(dayjs(record.startedAt), 'minute'))}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>devam ediyor</Text>
            </div>
          );
        }
        return formatDurationMinutes(record.durationMinutes);
      },
    },
    {
      title: 'Aktivite',
      key: 'activity',
      render: (_, record) => {
        const activity = record.activity;
        if (!activity) return <Text type="secondary">-</Text>;
        return (
          <div style={{ fontSize: 13, maxWidth: 320 }}>
            <div className="fw-medium">{activity.title}</div>
            {activity.description && (
              <div>
                <Text type="secondary">Nereye: </Text>
                <span style={{ whiteSpace: 'pre-line' }}>{activity.description}</span>
              </div>
            )}
            <div>
              <Text type="secondary">Araç: </Text>
              {activity.vehicle ? (
                <span>
                  <i className="ti ti-car me-1" />
                  {activity.vehicle.plateNumber}
                </span>
              ) : (
                <span>Araç yok</span>
              )}
            </div>
            {activity.completionNotes && (
              <div>
                <Text type="secondary">Sonuç: </Text>
                <span style={{ whiteSpace: 'pre-line' }}>{activity.completionNotes}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Onaylayan',
      key: 'approvedBy',
      render: (_, record) => (
        <div style={{ fontSize: 13 }}>
          <div>
            <Text type="secondary">Çıkış: </Text>
            {record.startedAt ? record.startApprovedBy || 'Otomatik onay' : '-'}
          </div>
          <div>
            <Text type="secondary">Dönüş: </Text>
            {record.endedAt ? record.endApprovedBy || 'Otomatik onay' : '-'}
          </div>
        </div>
      ),
    },
  ];

  return (
    <Table<FieldModeSession>
      rowKey="id"
      columns={columns}
      dataSource={sessions}
      loading={loading}
      size="middle"
      scroll={{ x: 'max-content' }}
      pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
      locale={{ emptyText: 'Seçili tarih aralığında saha kaydı bulunamadı' }}
    />
  );
};

export default FieldModeSessionsTable;
